import { type FC, useEffect, useMemo, useState } from 'react';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';

import { TYPES } from '@/engine/ioc';

import { useInjection } from 'inversify-react';
import { useHighlight } from '@/components/boxes/hooks/useHighlight.ts';
import { useSelect } from '@/components/boxes/hooks/useSelect.ts';
import { IndicationRow } from '@/components/boxes/indicationsBoxes/customIndications/IndicationRow.tsx';
import type { IBaseViewModel } from '@/engine/app/interfaces.ts';
import type { IndicationsBoxController } from '@/components/boxes/indicationsBoxes/indicationBoxController.ts';
import { observer } from 'mobx-react';
import { CollapsibleContent } from '@/components/boxes/indicationsBoxes/CollapsibleContent.tsx';
import { useGroupItemsVisibleState } from '@/components/boxes/hooks/useGroupItemsVisibleState.ts';
import { useSurgerySpecificUiConfigValue } from '@/engine/app/modules/ui';

interface IProps {
  indications: Array<IBaseViewModel>;
}

export const IndicationsBox: FC<IProps> = observer(({ indications }) => {
  const [isShown, setIsShown] = useState<boolean>(true);
  const mainController = useInjection<IndicationsBoxController>(TYPES.IndicationsBoxController);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);

  const { isHighlighted } = useHighlight(indications);
  const { isSelected } = useSelect(indications);
  const { visibilityState } = useGroupItemsVisibleState(indications);

  const areIndicationsImplicitlyEditable = useSurgerySpecificUiConfigValue<boolean>('areIndicationsImplicitlyEditable');

  useEffect(() => {
    setIsShown(!!indications.length);
  }, [indications.length]);

  const toggleIsCollapsed = (): void => {
    setIsCollapsed(!isCollapsed);
  };

  const allDescriptors = useMemo(() => indications.map((i) => i.descriptor), [indications]);

  return (
    isShown && (
      <TableContainer component={Paper}>
        <Table aria-label="a dense table" size="small">
          <TableBody>
            <CollapsibleContent
              visibilityState={visibilityState}
              beingEditedState={
                areIndicationsImplicitlyEditable
                  ? undefined
                  : mainController.checkGroupViewItemsBeingEdited(allDescriptors)
              }
              label={'Custom indications'}
              descriptors={allDescriptors}
              controller={mainController}
              isCollapsed={isCollapsed}
              toggleIsCollapsed={toggleIsCollapsed}
            >
              {indications.map((indication: IBaseViewModel, index: number) => {
                return (
                  <IndicationRow
                    key={index}
                    indication={indication}
                    mainController={mainController}
                    isSelected={isSelected}
                    isHighlighted={isHighlighted}
                    index={index}
                    isImplicitlyEditable={areIndicationsImplicitlyEditable}
                  />
                );
              })}
            </CollapsibleContent>
          </TableBody>
        </Table>
      </TableContainer>
    )
  );
});
